import dayjs, { Dayjs } from 'dayjs';
import React, { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { BsCalendar2 } from 'react-icons/bs';
import { FaChevronLeft, FaChevronRight } from 'react-icons/fa6';

/**
 * DateInput Component
 *
 * A fully-featured date picker component with a dropdown calendar interface.
 * Provides internationalized date selection with proper keyboard navigation and accessibility.
 *
 * Features:
 * - Calendar dropdown with month navigation
 * - Smart positioning (opens upward when not enough space below)
 * - Internationalized date formatting (English, Thai, Chinese)
 * - Today highlighting and selected date indication
 * - Out-of-month dates shown dimmed
 * - Disabled date support
 * - Keyboard navigation (Escape to close)
 * - Click outside to close
 * - Customizable validation states
 * - Form integration with react-hook-form
 *
 * Design System:
 * - Consistent with design system colors and typography
 * - Circular date buttons for modern appearance
 * - Proper focus and hover states
 * - Error state styling
 *
 * @example
 * Basic usage:
 * ```tsx
 * <DateInput
 *   label="Select Date"
 *   value={selectedDate}
 *   onChange={(date) => setSelectedDate(date)}
 *   placeholder="Choose a date"
 * />
 * ```
 *
 * @example
 * With validation and disabled dates:
 * ```tsx
 * <DateInput
 *   label="Appointment Date"
 *   value={appointmentDate}
 *   onChange={setAppointmentDate}
 *   disabledDates={[new Date('2024-01-01')]}
 *   minDate={new Date()}
 *   maxDate={new Date('2024-12-31')}
 *   error="Please select a valid date"
 *   required
 * />
 * ```
 *
 * @example
 * Form integration with react-hook-form:
 * ```tsx
 * <DateInput
 *   {...register('bookingDate')}
 *   label="Booking Date"
 *   error={errors.bookingDate?.message}
 *   disabled={isSubmitting}
 * />
 * ```
 */
export interface DateInputProps
  extends Omit<
    React.InputHTMLAttributes<HTMLInputElement>,
    'value' | 'onChange'
  > {
  label?: string;
  error?: string;
  value?: Date | null;
  onChange?: (date: Date | null) => void;
  minDate?: Date;
  maxDate?: Date;
  disabled?: boolean;
}

// Icons are provided by react-icons

const DateInput = ({
  ref,
  label,
  error,
  className = '',
  disabled,
  value,
  onChange,
  minDate,
  maxDate,
  ...props
}: DateInputProps & { ref?: React.RefObject<HTMLInputElement | null> }) => {
  const { t, i18n } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const [currentMonth, setCurrentMonth] = useState<Dayjs>(
    value ? dayjs(value) : dayjs(),
  );
  const [isFocused, setIsFocused] = useState(false);
  const [dropdownPosition, setDropdownPosition] = useState<'bottom' | 'top'>(
    'bottom',
  );
  const containerRef = useRef<HTMLDivElement>(null);
  const calendarRef = useRef<HTMLDivElement>(null);

  // Calculate dropdown position based on available space
  const calculateDropdownPosition = (): 'bottom' | 'top' => {
    if (!containerRef.current) return 'bottom';

    const container = containerRef.current;
    const containerRect = container.getBoundingClientRect();
    const viewportHeight = window.innerHeight;

    // Estimated calendar height (header + day names + 6 weeks + padding)
    const estimatedCalendarHeight = 350;

    const spaceBelow = viewportHeight - containerRect.bottom;
    const spaceAbove = containerRect.top;

    // If there's not enough space below but enough space above, open upward
    if (
      spaceBelow < estimatedCalendarHeight &&
      spaceAbove >= estimatedCalendarHeight
    ) {
      return 'top';
    }

    // Default to opening downward
    return 'bottom';
  };

  // Determine the actual state
  const actualState = disabled
    ? 'disabled'
    : error
      ? 'error'
      : isFocused
        ? 'focus'
        : 'default';

  // Close calendar when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      const clickedInsideInput = containerRef.current?.contains(target);
      const clickedInsideCalendar = calendarRef.current?.contains(target);

      if (!clickedInsideInput && !clickedInsideCalendar) {
        setIsOpen(false);
        setIsFocused(false);
      }
    };

    // Only add listener when calendar is open
    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen]);

  // Recalculate position on window resize and handle keyboard events
  useEffect(() => {
    const handleResize = () => {
      if (isOpen) {
        const position = calculateDropdownPosition();
        setDropdownPosition(position);
      }
    };

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && isOpen) {
        setIsOpen(false);
        setIsFocused(false);
      }
    };

    if (isOpen) {
      window.addEventListener('resize', handleResize);
      window.addEventListener('scroll', handleResize);
      document.addEventListener('keydown', handleKeyDown);
    }

    return () => {
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('scroll', handleResize);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen]);

  // Handle input focus/blur
  const handleInputClick = () => {
    if (!disabled) {
      const position = calculateDropdownPosition();
      setDropdownPosition(position);
      setIsOpen(true);
      setIsFocused(true);
    }
  };

  // Format date for display using dayjs
  const formatDate = (date: Date | null): string => {
    if (!date) return '';
    return dayjs(date).format('DD/MM/YYYY');
  };

  // Get month/year display
  const getMonthYearDisplay = (date: Dayjs): string => {
    try {
      return date.locale(i18n.language).format('MMMM YYYY');
    } catch {
      // fallback to Intl if locale not available in dayjs
      return new Date(date.toDate()).toLocaleDateString(i18n.language, {
        year: 'numeric',
        month: 'long',
      });
    }
  };

  // Get day names for calendar header
  const getDayNames = (): string[] => {
    const locale = i18n.language;
    // dayjs weekday starting from Sunday (0) to Saturday (6)
    const dayNames: string[] = [];
    for (let i = 0; i < 7; i++) {
      // pick a known Sunday and add i days
      const d = dayjs('2023-01-01').add(i, 'day');
      try {
        dayNames.push(d.locale(locale).format('ddd').toUpperCase());
      } catch {
        dayNames.push(d.format('ddd').toUpperCase());
      }
    }

    return dayNames;
  };

  // Generate calendar days (6 weeks) for a given month using dayjs
  const getCalendarDays = (month: Dayjs): Dayjs[] => {
    const firstDay = month.date(1);
    const startDate = firstDay.subtract(firstDay.day(), 'day');

    const days: Dayjs[] = [];
    for (let i = 0; i < 42; i++) {
      days.push(startDate.add(i, 'day'));
    }

    return days;
  };

  // Handle date selection (convert Dayjs -> Date for onChange)
  const handleDateSelect = (date: Dayjs) => {
    // Update internal state for uncontrolled usage
    if (!isControlled) {
      setInternalSelected(date);
    }

    // update parent and ensure calendar shows the selected month
    onChange?.(date.toDate());
    setCurrentMonth(date);
    setIsOpen(false);
    setIsFocused(false);
  };

  // Navigate months using dayjs
  const navigateMonth = (direction: 'prev' | 'next') => {
    setCurrentMonth((m) =>
      direction === 'prev' ? m.subtract(1, 'month') : m.add(1, 'month'),
    );
  };

  // Check if date is disabled (dayjs)
  const isDateDisabled = (date: Dayjs): boolean => {
    if (minDate && date.isBefore(dayjs(minDate), 'day')) return true;
    if (maxDate && date.isAfter(dayjs(maxDate), 'day')) return true;
    return false;
  };

  // Check if date is selected
  const isDateSelected = (date: Dayjs): boolean => {
    if (!value) return false;
    return date.isSame(dayjs(value), 'day');
  };

  // Check if date is today
  const isToday = (date: Dayjs): boolean => date.isSame(dayjs(), 'day');

  // Check if date is in current month
  const isInCurrentMonth = (date: Dayjs): boolean =>
    date.isSame(currentMonth, 'month');

  // Base styles following Figma design specifications
  const baseContainerStyles =
    'bg-white flex gap-2 h-12 items-center px-4 py-2 rounded-lg w-full border transition duration-200 cursor-pointer';

  // State-specific border and focus styles
  const stateStyles = {
    default:
      'border-border-default hover:border-border-hover focus-within:border-primary focus-within:ring-0.5 focus-within:ring-primary',
    focus: 'border-primary ring-0.5 ring-primary',
    error: 'border-error ring-0.5 ring-error',
    disabled: 'border-border-disabled bg-bg-disabled cursor-not-allowed',
  };

  // Text styles
  const textStyles = {
    default: 'text-primary-text',
    focus: 'text-primary-text',
    error: 'text-primary-text',
    disabled: 'text-border-disabled',
  };

  // Icon styles
  const iconStyles = {
    default: 'text-secondary-text',
    focus: 'text-primary',
    error: 'text-error',
    disabled: 'text-border-disabled',
  };

  const calendarDays = getCalendarDays(currentMonth);
  const dayNames = getDayNames();

  // Controlled vs uncontrolled: if `value` prop is provided (possibly null), treat as controlled.
  const isControlled = typeof value !== 'undefined';
  const [internalSelected, setInternalSelected] = useState<Dayjs | null>(
    value ? dayjs(value) : null,
  );

  // Keep internal selected in sync when `value` prop changes
  useEffect(() => {
    setInternalSelected(value ? dayjs(value) : null);
  }, [value]);

  return (
    <div className="relative flex w-full flex-col gap-1 p-0">
      {label && (
        <label className="text-secondary-text relative w-full shrink-0 text-left text-lg leading-7 font-medium not-italic">
          {label}
        </label>
      )}

      <div
        ref={containerRef}
        className={`${baseContainerStyles} ${stateStyles[actualState]}`}
        onClick={handleInputClick}
      >
        {/* Hidden input for form compatibility */}
        <input
          ref={ref}
          type="hidden"
          value={
            isControlled
              ? value
                ? value.toISOString().split('T')[0]
                : ''
              : internalSelected
                ? internalSelected.toDate().toISOString().split('T')[0]
                : ''
          }
          {...props}
        />

        {/* Display value */}
        <div className="relative flex flex-1 flex-col p-0">
          <div
            className={`relative w-full bg-transparent leading-7 font-normal ${textStyles[actualState]} ${className}`}
          >
            {isControlled
              ? value
                ? formatDate(value)
                : props.placeholder || t('calendar.selectDate', 'Select a date')
              : internalSelected
                ? internalSelected.format('DD/MM/YYYY')
                : props.placeholder ||
                  t('calendar.selectDate', 'Select a date')}
          </div>
        </div>

        {/* Calendar Icon */}
        <div
          className={`flex size-5 items-center justify-center p-0 ${iconStyles[actualState]} pointer-events-none`}
        >
          <BsCalendar2 className="size-5" />
        </div>
      </div>

      {/* Calendar Dropdown */}
      {isOpen && !disabled && (
        <div
          ref={calendarRef}
          className={`border-border-default absolute inset-x-0 z-50 rounded-lg border bg-white p-4 shadow-lg ${
            dropdownPosition === 'top' ? 'bottom-full mb-1' : 'top-full mt-1'
          }`}
        >
          {/* Calendar Header */}
          <div className="mb-4 flex items-center justify-between">
            <button
              type="button"
              onClick={() => navigateMonth('prev')}
              className="text-secondary-text hover:text-primary cursor-pointer rounded p-1 transition-colors hover:bg-gray-100"
            >
              <FaChevronLeft className="size-5" />
            </button>

            <div className="text-primary-text font-semibold">
              {getMonthYearDisplay(currentMonth)}
            </div>

            <button
              type="button"
              onClick={() => navigateMonth('next')}
              className="text-secondary-text hover:text-primary cursor-pointer rounded p-1 transition-colors hover:bg-gray-100"
            >
              <FaChevronRight className="size-5" />
            </button>
          </div>

          {/* Day Headers */}
          <div className="mb-2 grid grid-cols-7 gap-1">
            {dayNames.map((dayName, index) => (
              <div
                key={index}
                className="text-secondary-text p-2 text-center text-xs font-medium"
              >
                {dayName}
              </div>
            ))}
          </div>

          {/* Calendar Days */}
          <div className="grid grid-cols-7 gap-1">
            {calendarDays.map((d, index) => {
              const date = d; // Dayjs

              const disabledDay = isDateDisabled(date);
              const selected = isDateSelected(date);
              const today = isToday(date);

              return (
                <button
                  key={index}
                  type="button"
                  onClick={() => !disabledDay && handleDateSelect(date)}
                  disabled={disabledDay}
                  className={`h-10 w-10 rounded-full text-sm font-medium transition duration-200 ${
                    disabledDay
                      ? 'text-text-disabled cursor-not-allowed bg-transparent hover:bg-transparent'
                      : 'cursor-pointer ' +
                        (selected
                          ? 'bg-primary hover:bg-primary text-white'
                          : today
                            ? 'text-primary bg-blue-50 font-semibold hover:bg-blue-100'
                            : isInCurrentMonth(date)
                              ? 'text-primary-text hover:bg-gray-100'
                              : 'text-gray-400 hover:bg-gray-50')
                  }`}
                >
                  {date.date()}
                </button>
              );
            })}
          </div>
        </div>
      )}

      {error && <p className="text-error mt-1 text-xs leading-4">{error}</p>}
    </div>
  );
};

DateInput.displayName = 'DateInput';

export default DateInput;
